import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiEye, FiCheck, FiX, FiClock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { agencyAgreementsAPI, settingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { toast } from 'react-toastify';
import Modal from '../../components/Common/Modal';
import AuditTimeline from '../../components/Common/AuditTimeline';
import { UnifiedFilterHeaderCell, UnifiedTableToolbar } from '../../components/Common/UnifiedTableFilters';
import {
  runTableFilters,
  hasActiveClientTableFilters,
  CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT
} from '../../utils/tableFilters';

const AgreementList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getConfig } = useCountry();
  const countryConfig = getConfig();
  const requiresVerification = countryConfig.features.agreementVerification !== false;
  const isAdminUser = ['super_admin', 'admin', 'manager'].includes(String(user?.role || '').toLowerCase());
  const [loading, setLoading] = useState(true);
  const [agreements, setAgreements] = useState([]);
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });
  const [verifyingId, setVerifyingId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyAgreement, setHistoryAgreement] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);

  const formatAgreementStatus = (status) => {
    const s = String(status || '');
    const keyByStatus = {
      draft: 'agreements.draft',
      under_verification: 'agreements.underVerification',
      verified: 'agreements.verified',
      not_verified: 'agreements.notVerified'
    };
    return keyByStatus[s] ? t(keyByStatus[s]) : s.replace(/_/g, ' ');
  };

  const agreementColumnFilters = useMemo(() => {
    const statusOptions = Array.from(new Set(agreements.map((a) => String(a.status || '')).filter(Boolean)));
    const ownerOptions = Array.from(new Set(agreements.map((a) => String(a.created_by_name || '')).filter(Boolean)));
    return [
      { key: 'agency_name', label: t('agreements.tableAgency'), field: 'agency_name', type: 'text' },
      { key: 'lead_ref', label: t('agreements.tableRef'), field: 'lead_ref', type: 'text' },
      { key: 'agreement_date', label: t('agreements.tableAgreementDate'), field: 'agreement_date', type: 'date' },
      { key: 'director_name', label: t('agreements.tableDirector'), field: 'director_name', type: 'text' },
      { key: 'company_name', label: t('agreements.tableCompany'), field: 'company_name', type: 'text' },
      { key: 'status', label: t('agreements.tableStatus'), field: 'status', type: 'multi', options: statusOptions.map((s) => ({ value: s, label: formatAgreementStatus(s) })) },
      { key: 'created_by_name', label: t('agreements.tableCreatedBy'), field: 'created_by_name', type: 'multi', options: ownerOptions.map((s) => ({ value: s, label: s })) },
      { key: 'updated_at', label: t('agreements.tableUpdatedAt'), field: 'updated_at', type: 'date' }
    ];
  }, [agreements, t]);
  const clientTableFilterMode = useMemo(
    () => hasActiveClientTableFilters(tableFilters, agreementColumnFilters),
    [tableFilters, agreementColumnFilters]
  );

  const loadAgreements = useCallback(async () => {
    try {
      setLoading(true);
      const active = clientTableFilterMode;
      const res = await agencyAgreementsAPI.getAll({ limit: active ? CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT : 500 });
      setAgreements(res.data?.agreements || []);
    } catch (err) {
      toast.error(err.response?.data?.message || t('agreements.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [t, clientTableFilterMode]);

  useEffect(() => {
    loadAgreements();
  }, [loadAgreements]);

  const filteredRows = useMemo(
    () =>
      runTableFilters(
        agreements,
        tableFilters,
        tableFilters,
        ['agency_name', 'lead_ref', 'director_name', 'company_name', 'status', 'created_by_name'],
        agreementColumnFilters
      ),
    [agreements, tableFilters, agreementColumnFilters]
  );
  const updateColumnFilters = useCallback(
    (updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })),
    []
  );

  const handleVerifyAction = async (agreementId, decision) => {
    try {
      setVerifyingId(agreementId);
      await agencyAgreementsAPI.verify(agreementId, decision);
      toast.success(decision === 'verified' ? t('agreements.agreementVerified') : t('agreements.agreementNotVerified'));
      loadAgreements();
    } catch (err) {
      toast.error(err.response?.data?.message || t('agreements.verifyFailed'));
    } finally {
      setVerifyingId(null);
    }
  };

  const downloadGenerated = async (agreementId) => {
    try {
      const res = await agencyAgreementsAPI.getGeneratedUrl(agreementId);
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.response?.data?.message || t('agreements.generatedNotAvailable'));
    }
  };

  const downloadSigned = async (agreementId) => {
    try {
      const res = await agencyAgreementsAPI.getSignedUrl(agreementId);
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.response?.data?.message || t('agreements.signedNotAvailable'));
    }
  };

  const openHistory = async (agreement) => {
    if (!agreement?.agreement_id) return;
    setHistoryAgreement(agreement);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await settingsAPI.getAuditLog({
        entity_type: 'agreement',
        entity_id: agreement.agreement_id,
        limit: 200,
        page: 1
      });
      setHistoryLogs(res.data || []);
    } catch (err) {
      setHistoryLogs([]);
      toast.error(t('agreements.failedLoadHistory'));
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t('agreements.title')}</h1>
      </div>

      <div className="table-container">
        <div className="table-toolbar">
          <UnifiedTableToolbar
            draft={tableFilters}
            onDraftChange={setTableFilters}
            onReset={() => setTableFilters({ globalSearch: '', columns: {} })}
            hasActiveFilters={Boolean(tableFilters.globalSearch || Object.keys(tableFilters.columns || {}).length)}
            searchPlaceholder={t('agreements.searchPlaceholder')}
          />
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner"></div></div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <UnifiedFilterHeaderCell column={agreementColumnFilters[0]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={agreementColumnFilters[1]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={agreementColumnFilters[2]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={agreementColumnFilters[3]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={agreementColumnFilters[4]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={agreementColumnFilters[5]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <th>{t('agreements.tableGenerated')}</th>
                  <th>{t('agreements.tableSigned')}</th>
                  <UnifiedFilterHeaderCell column={agreementColumnFilters[6]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={agreementColumnFilters[7]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((a) => (
                  <tr key={a.agreement_id}>
                    <td>{a.agency_name || '-'}</td>
                    <td>
                      {a.lead_ref && a.lead_id ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/b2b-agencies/${a.lead_id}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: 'var(--primary)',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontWeight: 600
                          }}
                        >
                          {a.lead_ref}
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{a.agreement_date ? new Date(a.agreement_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                    <td>{a.director_name || '-'}</td>
                    <td>{a.company_name || '-'}</td>
                    <td>
                      <span className="badge" style={{
                        background:
                          a.status === 'verified'
                            ? 'var(--success-light)'
                            : a.status === 'not_verified'
                              ? 'var(--danger-light)'
                              : 'var(--warning-light)',
                        color:
                          a.status === 'verified'
                            ? 'var(--success)'
                            : a.status === 'not_verified'
                              ? 'var(--danger)'
                              : 'var(--warning)'
                      }}>
                        {formatAgreementStatus(a.status)}
                      </span>
                    </td>
                    <td>
                      {a.generated_file_path ? (
                        <button className="btn btn-outline btn-sm" onClick={() => downloadGenerated(a.agreement_id)}>
                          <FiEye /> {t('agreements.draftBtn')}
                        </button>
                      ) : '-'}
                    </td>
                    <td>
                      {a.signed_file_path ? (
                        <button className="btn btn-outline btn-sm" onClick={() => downloadSigned(a.agreement_id)}>
                          <FiEye /> {t('agreements.signedBtn')}
                        </button>
                      ) : '-'}
                    </td>
                    <td>{a.created_by_name || '-'}</td>
                    <td>{a.updated_at ? new Date(a.updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                    <td>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <button type="button" className="btn btn-sm btn-outline" onClick={() => openHistory(a)}>
                          <FiClock /> {t('common.history')}
                        </button>
                        {isAdminUser && requiresVerification && a.status === 'under_verification' ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              disabled={verifyingId === a.agreement_id}
                              onClick={() => handleVerifyAction(a.agreement_id, 'verified')}
                            >
                              <FiCheck /> {t('agreements.verifyBtn')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline"
                              disabled={verifyingId === a.agreement_id}
                              onClick={() => handleVerifyAction(a.agreement_id, 'not_verified')}
                            >
                              <FiX /> {t('agreements.notVerifyBtn')}
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan="11" className="text-center" style={{ padding: 24, color: 'var(--text-muted)' }}>
                      {t('agreements.noAgreements')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryAgreement(null);
          setHistoryLogs([]);
          setHistoryLoading(false);
        }}
        title={
          historyAgreement
            ? t('agreements.historyTitle', {
                company: historyAgreement.company_name || `#${historyAgreement.agreement_id}`
              })
            : t('agreements.historyTitleFallback')
        }
        size="lg"
        footer={<button className="btn btn-outline" onClick={() => setHistoryOpen(false)}>{t('common.close')}</button>}
      >
        {historyLoading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : (
          <AuditTimeline logs={historyLogs} showEntity={false} />
        )}
      </Modal>
    </div>
  );
};

export default AgreementList;
